import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardBody,
  Table,
  Button,
  Row,
  Col,
  Spinner,
  Alert,
  Container,
  Badge,
  Input
} from 'reactstrap';
import { useAuth } from '../../contexts/AuthContext';
import dealService from '../../service/dealService.ts';
import SearchFilters from './components/SearchFilters';
import { formatNumber } from '../../utils/formatUtils';

const DealsList = () => {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [statusCounts, setStatusCounts] = useState({
    SCREENER: 0,
    OFFERS: 0,
    CONTRACT: 0,
    PRE_FUNDING: 0,
    COLLECTIONS: 0,
    DECLINED: 0
  });

  // Search functionality
  const [filters, setFilters] = useState({});
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [simpleSearch, setSimpleSearch] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedSubstatus, setSelectedSubstatus] = useState('');
  const [substatusCounts, setSubstatusCounts] = useState({});
  
  // Control initial load and prevent double requests
  const initialLoadDone = useRef(false);
  const searchDebounceTimer = useRef(null);

  // Status options for filter bubbles
  const statusOptions = [
    { value: 'SCREENER', label: 'Screener', color: '#5e72e4' },
    { value: 'OFFERS', label: 'Offers', color: '#5e72e4' },
    { value: 'CONTRACT', label: 'Contract', color: '#5e72e4' },
    { value: 'PRE_FUNDING', label: 'Pre Funding', color: '#5e72e4' },
    { value: 'COLLECTIONS', label: 'Collections', color: '#5e72e4' },
    { value: 'DECLINED', label: 'Declined', color: '#5e72e4' }
  ];

  // Substatus options by status
  const substatusOptions = {
    'SCREENER': [
      { value: 'READY', label: 'Ready' }
    ],
    'OFFERS': [
      { value: 'OFFERS_SENT', label: 'Offers Sent' }
    ],
    'CONTRACT': [
      { value: 'CONTRACT_SENT', label: 'Contract Sent' },
      { value: 'CONTRACT_PARTIALLY_SIGNED', label: 'Partially Signed' },
      { value: 'DECLINED', label: 'Declined' }
    ],
    'PRE_FUNDING': [
      { value: 'PENDING_PLAN', label: 'Pending Plan' },
      { value: 'PENDING_WIRE', label: 'Pending Wire' }
    ],
    'COLLECTIONS': [
      { value: 'FUNDED', label: 'Funded' },
      { value: 'EXPIRED_PAYMENTS', label: 'Expired Payments' },
      { value: 'CANCELLED', label: 'Cancelled' }
    ]
  };

  // Load deals on initial mount only
  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      loadDeals();
    }
    
    // Cleanup debounce timer on unmount
    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
    };
  }, []);
  
  // Load deals when page changes (after initial load)
  useEffect(() => {
    if (!initialLoadDone.current) return;
    if (Object.keys(filters).length > 0 || selectedStatus || selectedSubstatus || simpleSearch.trim()) return;
    if (currentPage === 0) return; // Skip page 0 as it's handled by initial load
    
    loadDeals();
  }, [currentPage]);

  // Load status counts on component mount
  useEffect(() => {
    const loadStatusCounts = async () => {
      try {
        const token = getToken();
        const response = await dealService.getDealsCounts(token, 'status');
        const countsData = response.data;
        
        console.log('📊 Status counts response:', countsData);
        
        // Initialize with zeros for all expected statuses
        const newStatusCounts = {
          SCREENER: 0,
          OFFERS: 0,
          CONTRACT: 0,
          PRE_FUNDING: 0,
          COLLECTIONS: 0,
          DECLINED: 0
        };
        
        // Populate with actual counts from API response
        if (Array.isArray(countsData)) {
          countsData.forEach(item => {
            if (item.status && typeof item.count === 'number') {
              newStatusCounts[item.status] = item.count;
            }
          });
        }
        
        setStatusCounts(newStatusCounts);
      } catch (error) {
        console.error('Error loading status counts:', error);
        // Keep default zero counts on error
      }
    };
    loadStatusCounts();
  }, [getToken]);

  // Load substatus counts when a status is selected
  const loadSubstatusCounts = async (status) => {
    try {
      const token = getToken();
      const response = await dealService.getSubstatusCounts(token, status);
      const countsData = response.data;
      
      console.log('📊 Substatus counts response for', status, ':', countsData);
      
      // Initialize with zeros for all expected substatus for this status
      const newSubstatusCounts = {};
      const statusSubstatuses = substatusOptions[status] || [];
      statusSubstatuses.forEach(substatus => {
        newSubstatusCounts[substatus.value] = 0;
      });
      
      // Populate with actual counts from API response
      if (Array.isArray(countsData)) {
        countsData.forEach(item => {
          if (item.substatus && typeof item.count === 'number') {
            newSubstatusCounts[item.substatus] = item.count;
          }
        });
      }
      
      setSubstatusCounts(newSubstatusCounts);
    } catch (error) {
      console.error('Error loading substatus counts:', error);
      // Keep default zero counts on error
      setSubstatusCounts({});
    }
  };

  const loadDeals = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = getToken();
      const response = await dealService.getAllDeals(token, currentPage, itemsPerPage);
      const data = response.data;
      
      console.log('📊 DealsList: API response:', data);
      
      // Handle pagination response
      let dealsArray = [];
      let newHasNextPage = false;
      let newHasPreviousPage = false;
      let newTotalElements = 0;
      
      if (data && data.items && Array.isArray(data.items)) {
        // Handle response with "items" array structure
        dealsArray = data.items;
        newTotalElements = data.totalElements || data.totalCount || data.items.length;
        newHasNextPage = data.hasNext || (data.items.length === itemsPerPage);
        newHasPreviousPage = currentPage > 0;
      } else if (data && data.content && Array.isArray(data.content)) {
        // Handle Spring pagination format
        dealsArray = data.content;
        newTotalElements = data.totalElements || data.content.length;
        newHasNextPage = !data.last;
        newHasPreviousPage = currentPage > 0;
      } else if (Array.isArray(data)) {
        // Handle direct array response
        dealsArray = data;
        newHasNextPage = data.length === itemsPerPage;
        newHasPreviousPage = currentPage > 0;
        newTotalElements = data.length;
      }
      
      setDeals(dealsArray);
      setHasNextPage(newHasNextPage);
      setHasPreviousPage(newHasPreviousPage);
      setTotalElements(newTotalElements);
      setTotalPages(Math.max(1, Math.ceil((newTotalElements || 0) / itemsPerPage)));
      
    } catch (err) {
      console.error('Error loading deals:', err);
      setError('Error loading deals: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Search functionality
  const handleFilterChange = (newFilters) => {
    console.log('🔄 handleFilterChange called with:', newFilters);
    setFilters(newFilters);
  };

  const handleSearch = async (searchFilters = filters, page = 0, size = itemsPerPage) => {
    console.log('🚀 handleSearch called with filters:', searchFilters);
    setError(null);
    setLoading(true);

    try {
      const token = getToken();
      let response;
      
      // Check if there are any search filters
      const hasFilters = searchFilters.q || 
                        (searchFilters.status && searchFilters.status.length > 0) ||
                        (searchFilters.substatus && searchFilters.substatus.length > 0) ||
                        searchFilters.legalName ||
                        searchFilters.federalTaxId ||
                        searchFilters.createdBy ||
                        searchFilters.createdAtFrom ||
                        searchFilters.createdAtTo;

      if (hasFilters) {
        // Use advanced search
        response = await dealService.advancedSearchDeals(token, {
          ...searchFilters,
          page,
          size
        });
      } else {
        // Use regular load
        response = await dealService.getAllDeals(token, page, size);
      }
      
      const data = response.data;
      
      let dealsArray = [];
      let newHasNextPage = false;
      let newHasPreviousPage = false;
      let newTotalElements = 0;
      let newTotalPages = 0;
      
      if (data && data.items && Array.isArray(data.items)) {
        // Handle response with "items" array structure
        dealsArray = data.items;
        newTotalElements = data.totalElements || data.totalCount || data.items.length;
        newTotalPages = data.totalPages || Math.ceil(newTotalElements / size) || 1;
        newHasNextPage = data.hasNext || (data.items.length === size);
        newHasPreviousPage = page > 0;
      } else if (data && data.content && Array.isArray(data.content)) {
        // Handle Spring pagination format
        dealsArray = data.content;
        newTotalElements = data.totalElements || data.content.length;
        newTotalPages = data.totalPages || Math.ceil(newTotalElements / size) || 1;
        newHasNextPage = !data.last;
        newHasPreviousPage = page > 0;
      } else if (Array.isArray(data)) {
        // Handle direct array response
        dealsArray = data;
        newTotalElements = data.length;
        newTotalPages = Math.ceil(newTotalElements / size) || 1;
        newHasNextPage = data.length === size;
        newHasPreviousPage = page > 0;
      }
      
      setDeals(dealsArray);
      setHasNextPage(newHasNextPage);
      setHasPreviousPage(newHasPreviousPage);
      setTotalElements(newTotalElements);
      setTotalPages(newTotalPages);
      setCurrentPage(page);
      
    } catch (error) {
      console.error('Search failed:', error);
      setError(`Search failed: ${error.message}`);
      await loadDeals();
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFilters({});
    setSimpleSearch('');
    setSelectedStatus('');
    setSelectedSubstatus('');
    setSubstatusCounts({});
    setError(null);
    setCurrentPage(0);
    loadDeals();
  };

  const toggleAdvancedSearch = () => {
    setShowAdvancedSearch(!showAdvancedSearch);
  };

  const handleSimpleSearch = (searchTerm) => {
    setSimpleSearch(searchTerm);
    
    // Clear any existing timer
    if (searchDebounceTimer.current) {
      clearTimeout(searchDebounceTimer.current);
    }
    
    // Only debounce if initial load is done
    if (initialLoadDone.current) {
      searchDebounceTimer.current = setTimeout(() => {
        const query = searchTerm.trim();
        
        if (!query) {
          // If empty, clear filters and reset
          setFilters({});
          setSelectedStatus('');
          setSelectedSubstatus('');
          setCurrentPage(0);
          loadDeals();
        } else {
          // Perform simple search
          const simpleFilters = { q: query };
          setFilters(simpleFilters);
          setSelectedStatus('');
          setSelectedSubstatus('');
          handleSearch(simpleFilters, 0, itemsPerPage);
        }
      }, 500);
    }
  };

  const handleStatusFilter = (status) => {
    console.log('🏷️ Status filter clicked:', status);
    
    // Toggle status filter
    if (selectedStatus === status) {
      // If clicking the same status, clear the filter
      console.log('🔄 Clearing status filter...');
      setSelectedStatus('');
      setSelectedSubstatus('');
      setSubstatusCounts({});
      setFilters({});
      setCurrentPage(0);
      setSimpleSearch('');
      // Don't call loadDeals directly - let useEffect handle it
    } else {
      // Apply new status filter
      setSelectedStatus(status);
      setSelectedSubstatus(''); // Clear any selected substatus
      const statusFilters = {
        status: [status]
      };
      setFilters(statusFilters);
      setCurrentPage(0);
      
      // Load substatus counts for the selected status
      loadSubstatusCounts(status);
      
      handleSearch(statusFilters, 0, itemsPerPage);
    }
  };

  const handleSubstatusFilter = async (substatus) => {
    console.log('🏷️ Substatus filter clicked:', substatus);
    
    // Toggle substatus filter
    if (selectedSubstatus === substatus) {
      // If clicking the same substatus, clear the substatus filter but keep status filter
      console.log('🔄 Clearing substatus filter...');
      setSelectedSubstatus('');
      const statusFilters = {
        status: [selectedStatus]
      };
      setFilters(statusFilters);
      setCurrentPage(0);
      handleSearch(statusFilters, 0, itemsPerPage);
    } else {
      // Apply new substatus filter using specific endpoint
      setSelectedSubstatus(substatus);
      setCurrentPage(0);
      setLoading(true);
      setError(null);

      try {
        const token = getToken();
        const response = await dealService.searchBySubstatus(token, selectedStatus, substatus, 0, itemsPerPage);
        const data = response.data;
        
        console.log('📊 Substatus search response:', data);
        
        let dealsArray = [];
        let newHasNextPage = false;
        let newHasPreviousPage = false;
        let newTotalElements = 0;
        let newTotalPages = 0;
        
        if (data && data.items && Array.isArray(data.items)) {
          // Handle response with "items" array structure
          dealsArray = data.items;
          newTotalElements = data.totalElements || data.totalCount || data.items.length;
          newTotalPages = data.totalPages || Math.ceil(newTotalElements / itemsPerPage) || 1;
          newHasNextPage = data.hasNext || (data.items.length === itemsPerPage);
          newHasPreviousPage = false; // First page
        } else if (data && data.content && Array.isArray(data.content)) {
          // Handle Spring pagination format
          dealsArray = data.content;
          newTotalElements = data.totalElements || data.content.length;
          newTotalPages = data.totalPages || Math.ceil(newTotalElements / itemsPerPage) || 1;
          newHasNextPage = !data.last;
          newHasPreviousPage = false; // First page
        } else if (Array.isArray(data)) {
          // Handle direct array response
          dealsArray = data;
          newTotalElements = data.length;
          newTotalPages = Math.ceil(newTotalElements / itemsPerPage) || 1;
          newHasNextPage = data.length === itemsPerPage;
          newHasPreviousPage = false; // First page
        }
        
        setDeals(dealsArray);
        setHasNextPage(newHasNextPage);
        setHasPreviousPage(newHasPreviousPage);
        setTotalElements(newTotalElements);
        setTotalPages(newTotalPages);
        setCurrentPage(0);
        
        // Update filters state to reflect current selection
        const statusSubstatusFilters = {
          status: [selectedStatus],
          substatus: [substatus]
        };
        setFilters(statusSubstatusFilters);
        
      } catch (error) {
        console.error('Substatus search failed:', error);
        setError(`Substatus search failed: ${error.message}`);
        // Fallback to regular status search
        const statusFilters = {
          status: [selectedStatus]
        };
        setFilters(statusFilters);
        handleSearch(statusFilters, 0, itemsPerPage);
      } finally {
        setLoading(false);
      }
    }
  };

  // Pagination functions
  const handlePageChange = async (pageNumber) => {
    if (pageNumber < 0) return;
    if (totalPages && pageNumber >= totalPages) return;
    setLoading(true);
    
    // Check if we have a substatus filter active
    if (selectedStatus && selectedSubstatus) {
      // Use specific substatus endpoint for pagination
      try {
        const token = getToken();
        const response = await dealService.searchBySubstatus(token, selectedStatus, selectedSubstatus, pageNumber, itemsPerPage);
        const data = response.data;
        
        let dealsArray = [];
        let newHasNextPage = false;
        let newHasPreviousPage = false;
        let newTotalElements = 0;
        let newTotalPages = 0;
        
        if (data && data.items && Array.isArray(data.items)) {
          dealsArray = data.items;
          newTotalElements = data.totalElements || data.totalCount || data.items.length;
          newTotalPages = data.totalPages || Math.ceil(newTotalElements / itemsPerPage) || 1;
          newHasNextPage = data.hasNext || (data.items.length === itemsPerPage);
          newHasPreviousPage = pageNumber > 0;
        } else if (data && data.content && Array.isArray(data.content)) {
          dealsArray = data.content;
          newTotalElements = data.totalElements || data.content.length;
          newTotalPages = data.totalPages || Math.ceil(newTotalElements / itemsPerPage) || 1;
          newHasNextPage = !data.last;
          newHasPreviousPage = pageNumber > 0;
        } else if (Array.isArray(data)) {
          dealsArray = data;
          newTotalElements = data.length;
          newTotalPages = Math.ceil(newTotalElements / itemsPerPage) || 1;
          newHasNextPage = data.length === itemsPerPage;
          newHasPreviousPage = pageNumber > 0;
        }
        
        setDeals(dealsArray);
        setHasNextPage(newHasNextPage);
        setHasPreviousPage(newHasPreviousPage);
        setTotalElements(newTotalElements);
        setTotalPages(newTotalPages);
        setCurrentPage(pageNumber);
        
      } catch (error) {
        console.error('Substatus pagination failed:', error);
        setError(`Pagination failed: ${error.message}`);
      } finally {
        setLoading(false);
      }
    } else {
      // Use regular search for other cases
      if (Object.keys(filters).length > 0) {
        handleSearch(filters, pageNumber, itemsPerPage);
      } else {
        // For unfiltered results, still use handleSearch to maintain consistency
        handleSearch({}, pageNumber, itemsPerPage);
      }
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (hasNextPage) {
      handlePageChange(currentPage + 1);
    }
  };

  const handleViewDeal = (deal) => {
    navigate(`/deals/${deal.publicId}`);
  };

  const formatCurrency = (amount) => {
    return formatNumber(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'N/A';
    }
  };



  if (loading && deals.length === 0) {
    return (
      <Container className="mt-4 h-auto" fluid>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner color="primary" />
          <span className="ml-2">Loading deals...</span>
        </div>
      </Container>
    );
  }

  return (
    <>
      <style>
        {`
          @media (max-width: 767.98px) {
            .table-responsive {
              font-size: 0.875rem;
            }
            .compact-text {
              font-size: 0.8rem !important;
            }
            .compact-badge {
              font-size: 0.6rem !important;
              padding: 0.1rem 0.25rem !important;
            }
            .compact-icon {
              width: 24px !important;
              height: 24px !important;
              font-size: 0.7rem !important;
            }
            .btn-icon {
              padding: 0.5rem 0.75rem !important;
              font-size: 0.875rem !important;
              min-height: 38px !important;
              border-radius: 8px !important;
            }
            .card-header .btn-icon {
              width: 100% !important;
              justify-content: center !important;
              margin-bottom: 0.5rem !important;
              align-items: center !important;
              display: flex !important;
            }
          }
          .table.table-hover tbody tr:hover {
            background-color: rgba(25, 118, 210, 0.12) !important;
          }
        `}
      </style>
      <Container className="mt-4 h-auto" fluid>
        {/* Header Section */}
        <Row className="mb-4">
          <Col>
            <Card className="bg-gradient-info text-white mb-0">
              <CardBody className="py-3">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
                  <div className="mb-2 mb-md-0">
                    <h3 className="text-white mb-0">
                      <i className="ni ni-money-coins mr-2" />
                      Deals
                    </h3>
                    <p className="text-white mb-0 mt-1" style={{ opacity: 0.9 }}>
                      Manage and track deal submissions
                      {totalElements > 0 && (
                        <span className="ml-2 badge badge-light">
                          {formatNumber(totalElements)} deal{totalElements !== 1 ? 's' : ''}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="d-flex flex-column flex-sm-row gap-2">
                    <Button 
                      color="light" 
                      size="sm" 
                      onClick={toggleAdvancedSearch}
                      style={{ backgroundColor: '#e3f2fd', borderColor: '#bbdefb', color: '#1976d2' }}
                      className="mb-2 mb-sm-0"
                    >
                      <i className={`ni ${showAdvancedSearch ? 'ni-fat-remove' : 'ni-fat-add'} mr-2`}></i>
                      <span className="d-none d-sm-inline">
                        {showAdvancedSearch ? 'Hide Advanced Search' : 'Advanced Search'}
                      </span>
                      <span className="d-inline d-sm-none">
                        {showAdvancedSearch ? 'Hide Search' : 'Advanced'}
                      </span>
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

         {/* Simple Search Section with Status Filters */}
         <Row className="mb-3">
           <Col lg="5" md="12" className="mb-3 mb-lg-0">
             <div style={{ maxWidth: '350px', width: '100%' }}>
               <div className="position-relative">
                 <Input
                   type="text"
                   placeholder="Search deals..."
                   value={simpleSearch}
                   onChange={(e) => handleSimpleSearch(e.target.value)}
                   style={{
                     backgroundColor: '#ffffff',
                     border: '1px solid #e9ecef',
                     borderRadius: '25px',
                     padding: '12px 20px',
                     fontSize: '0.9rem',
                     boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                     width: '100%'
                   }}
                 />
                 <div className="position-absolute" style={{ right: '15px', top: '50%', transform: 'translateY(-50%)' }}>
                   <i className="ni ni-zoom-split-in text-muted" style={{ fontSize: '0.9rem' }}></i>
                 </div>
                 {simpleSearch && (
                   <Button
                     color="link"
                     size="sm"
                     onClick={() => handleSimpleSearch('')}
                     className="position-absolute p-0"
                     style={{ 
                       right: '40px', 
                       top: '50%', 
                       transform: 'translateY(-50%)',
                       color: '#6c757d',
                       fontSize: '0.8rem'
                     }}
                     title="Clear search"
                   >
                     <i className="ni ni-fat-remove"></i>
                   </Button>
                 )}
               </div>
             </div>
           </Col>
           <Col lg="7" md="12">
             <div className="status-filters-container d-flex justify-content-center">
               <div className="d-flex align-items-center flex-nowrap">
                 <span className="text-muted mr-2 status-label-text">
                   Filter by step:
                 </span>
                 <div className="status-filters-wrapper">
                   <div className="status-filters-scroll">
                     {statusOptions.map((status) => (
                       <button
                         key={status.value}
                         onClick={() => handleStatusFilter(status.value)}
                         className={`status-filter-pill compact ${selectedStatus === status.value ? 'active' : ''}`}
                       >
                         <span className="status-label">{status.label}</span>
                         <span className="status-badge">{formatNumber(statusCounts[status.value] ?? 0)}</span>
                       </button>
                     ))}
                      {selectedStatus && (
                        <button
                          onClick={() => {
                            console.log('🔄 Clear button clicked, clearing filters...');
                            setSelectedStatus('');
                            setSelectedSubstatus('');
                            setSubstatusCounts({});
                            setFilters({});
                            setCurrentPage(0);
                            setSimpleSearch('');
                            // Don't call loadDeals directly - let useEffect handle it
                          }}
                          className="status-filter-pill clear-btn compact"
                        >
                          <i className="ni ni-fat-remove"></i>
                          <span>Clear</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          {/* Substatus Filters Row - Only show when a status is selected and has more than 1 substatus option */}
          {selectedStatus && substatusOptions[selectedStatus] && substatusOptions[selectedStatus].length > 1 && (
            <Row className="mb-3">
              <Col lg="5" md="12" className="mb-3 mb-lg-0">
                {/* Empty space to align with search box above */}
              </Col>
              <Col lg="7" md="12">
                <div className="status-filters-container d-flex justify-content-center">
                  <div className="d-flex align-items-center flex-nowrap">
                    <span className="text-muted mr-2 status-label-text">
                      Filter by status ({statusOptions.find(s => s.value === selectedStatus)?.label}):
                    </span>
                    <div className="status-filters-wrapper">
                      <div className="status-filters-scroll d-flex align-items-center">
                        {substatusOptions[selectedStatus].map((substatus) => (
                          <button
                            key={substatus.value}
                            onClick={() => handleSubstatusFilter(substatus.value)}
                            className={`status-filter-pill compact ${selectedSubstatus === substatus.value ? 'active' : ''}`}
                            style={{ 
                              backgroundColor: selectedSubstatus === substatus.value ? '#5e72e4' : '#f8f9fa',
                              color: selectedSubstatus === substatus.value ? 'white' : '#495057',
                              border: selectedSubstatus === substatus.value ? '1px solid #5e72e4' : '1px solid #dee2e6'
                            }}
                          >
                            <span className="status-label">{substatus.label}</span>
                            <span className="status-badge">{formatNumber(substatusCounts[substatus.value] ?? 0)}</span>
                          </button>
                        ))}
                        {/* Always show Clear button placeholder to maintain alignment, visible only when substatus is selected */}
                        <button
                          onClick={() => {
                            console.log('🔄 Clear substatus button clicked...');
                            setSelectedSubstatus('');
                            const statusFilters = {
                              status: [selectedStatus]
                            };
                            setFilters(statusFilters);
                            setCurrentPage(0);
                            handleSearch(statusFilters, 0, itemsPerPage);
                          }}
                          className="status-filter-pill clear-btn compact"
                          style={{ 
                            visibility: selectedSubstatus ? 'visible' : 'hidden',
                            opacity: selectedSubstatus ? 1 : 0
                          }}
                        >
                          <i className="ni ni-fat-remove"></i>
                          <span>Clear</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          )}

        {/* Search Criteria Section */}
        {showAdvancedSearch && (
          <Row className="mb-4">
            <Col>
              <Card className="shadow">
                <CardHeader className="border-0">
                  <h3 className="mb-0">Search Criteria</h3>
                </CardHeader>
                <CardBody>
                  <SearchFilters 
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onSearch={handleSearch}
                    onClear={handleClear}
                    isLoading={loading}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* Deals List Section */}
        <Row>
          <Col>
            <Card className="shadow">
              <CardBody className="px-0 pt-0" style={{ position: 'relative' }}>
                {loading && deals.length > 0 && (
                  <div className="applications-overlay" style={{ position: 'absolute', top:0, left:0, right:0, bottom:0, background: 'rgba(255,255,255,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2 }}>
                    <div className="d-flex align-items-center">
                      <Spinner color="primary" />
                      <span className="ml-2 text-muted">Loading deals...</span>
                    </div>
                  </div>
                )}
                {error && (
                  <Alert color="danger" className="mx-3 mt-3">
                    <i className="fas fa-exclamation-triangle mr-2" />
                    {error}
                  </Alert>
                )}
                
                {!loading && deals.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="mb-4">
                      <i className="ni ni-money-coins text-muted" style={{ fontSize: '4rem', opacity: 0.3 }} />
                    </div>
                    <h4 className="text-muted mb-2">No deals found</h4>
                    <p className="text-muted mb-4">No deals match your current search criteria.</p>
                  </div>
                ) : (!loading && (
                  <>
                    {/* Desktop Table View */}
                    <div className="d-none d-md-block">
                      <div className="table-responsive" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                        <Table className="table table-hover" responsive style={{ minWidth: '1100px', whiteSpace: 'nowrap' }}>
                        <thead className="thead-light">
                          <tr>
                            <th scope="col" className="border-0" style={{ width: '15%' }}>
                              <div className="d-flex align-items-center compact-text">
                                ID
                              </div>
                            </th>
                            <th scope="col" className="border-0" style={{ width: '25%' }}>
                              <div className="d-flex align-items-center compact-text">
                                Business
                              </div>
                            </th>
                            <th scope="col" className="border-0" style={{ width: '15%' }}>
                              <div className="d-flex align-items-center compact-text">
                                Payback
                              </div>
                            </th>
                            <th scope="col" className="border-0" style={{ width: '15%' }}>
                              <div className="d-flex align-items-center compact-text">
                                Remaining
                              </div>
                            </th>
                            <th scope="col" className="border-0" style={{ width: '12%' }}>
                              <div className="d-flex align-items-center compact-text">
                                STEP/STATUS
                              </div>
                            </th>
                            <th scope="col" className="border-0" style={{ width: '8%' }}>
                              <div className="d-flex align-items-center compact-text">
                                Created
                              </div>
                            </th>
                            <th scope="col" className="border-0" style={{ width: '10%' }}>
                              <div className="d-flex align-items-center compact-text">
                                Funded Date
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {deals.map((deal, index) => (
                            <tr
                              key={deal.publicId || index}
                              className="border-bottom"
                              onClick={() => handleViewDeal(deal)}
                              style={{ cursor: 'pointer' }}
                            >
                              <td className="border-0">
                                <div className="d-flex align-items-center">
                                  <div className="compact-text">
                                    <span 
                                      className="font-weight-bold text-dark"
                                      title="Click to view deal details"
                                    >
                                      {deal.publicId || 'N/A'}
                                    </span>
                                  </div>
                                </div>
                              </td>
                              <td className="border-0">
                                <div className="d-flex align-items-center">
                                  <div className="compact-text">
                                    <div className="font-weight-bold text-dark mb-1">
                                      {deal.legalName || 'N/A'}
                                    </div>
                                    <small className="text-muted">
                                      {deal.industry || 'N/A'}
                                    </small>
                                    {deal.federalTaxId && (
                                      <small className="text-muted d-block">
                                        Tax ID: {deal.federalTaxId}
                                      </small>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="border-0">
                                <div className="d-flex align-items-center">
                                  <div className="compact-text">
                                    <span className="font-weight-bold text-dark">
                                      {formatCurrency(deal.payback || 0)}
                                    </span>
                                  </div>
                                </div>
                              </td>
                              <td className="border-0">
                                <div className="d-flex align-items-center">
                                  <div className="compact-text">
                                    <span className="font-weight-bold text-dark">
                                      {formatCurrency(deal.remainingAmount || 0)}
                                    </span>
                                  </div>
                                </div>
                              </td>
                              <td className="border-0">
                                <div className="compact-text">
                                  <div className="d-flex flex-column">
                                    <Badge 
                                      color="light" 
                                      className="compact-badge mb-1"
                                      style={{ backgroundColor: '#f8f9fa', color: '#495057', border: '1px solid #dee2e6' }}
                                    >
                                      {deal.status || 'N/A'}
                                    </Badge>
                                     {deal.substatus && deal.status !== 'DECLINED' && (
                                       <Badge 
                                         color="secondary" 
                                         className="compact-badge"
                                         style={{ backgroundColor: '#9ca3af', color: 'white', marginLeft: '-0.1px' }}
                                       >
                                         {deal.substatus}
                                       </Badge>
                                     )}
                                  </div>
                                  {deal.metadata?.origin && (
                                    <small className="text-muted d-block mt-1">
                                      <i className="fas fa-tag mr-1" />
                                      {deal.metadata.origin}
                                    </small>
                                  )}
                                </div>
                              </td>
                              <td className="border-0">
                                <div className="d-flex align-items-center">
                                  <div className="compact-text">
                                    <div className="font-weight-bold text-dark mb-1">
                                      {formatDate(deal.createdDate)}
                                    </div>
                                    <small className="text-muted">
                                      {deal.createdBy || 'System'}
                                    </small>
                                  </div>
                                </div>
                              </td>
                              <td className="border-0">
                                <div className="d-flex align-items-center">
                                  <div className="compact-text">
                                    <div className="font-weight-bold text-dark mb-1">
                                      {formatDate(deal.fundedAt)}
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        </Table>
                      </div>
                    </div>

                    {/* Mobile Card View */}
                    <div className="d-md-none">
                      <div className="row">
                        {deals.map((deal, index) => (
                          <div key={deal.publicId || index} className="col-12 mb-3">
                            <Card className="shadow-sm border-0">
                              <CardBody className="p-3">
                                {/* Header with Business Info and ID */}
                                <div className="mb-3">
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span 
                                      className="font-weight-bold text-dark"
                                      style={{ cursor: 'pointer', fontSize: '0.9rem' }}
                                      onClick={() => handleViewDeal(deal)}
                                      title="Click to view deal details"
                                    >
                                      ID: {deal.publicId || 'N/A'}
                                    </span>
                                  </div>
                                  <div>
                                    <h6 className="mb-1 font-weight-bold text-dark">
                                      {deal.legalName || 'N/A'}
                                    </h6>
                                    <div className="d-flex align-items-center mb-1">
                                      <small className="text-muted">
                                        {deal.industry || 'N/A'}
                                      </small>
                                    </div>
                                    {deal.federalTaxId && (
                                      <small className="text-muted">
                                        Tax ID: {deal.federalTaxId}
                                      </small>
                                    )}
                                  </div>
                                </div>

                                {/* Amount and Status Row */}
                                <div className="row mb-3">
                                  <div className="col-6">
                                    <div className="d-flex align-items-center">
                                      <div>
                                        <small className="text-muted d-block">Requested</small>
                                        <span className="font-weight-bold text-dark">
                                          {formatCurrency(deal.requestedAmount || 0)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-6">
                                    <div>
                                      <small className="text-muted d-block">Remaining</small>
                                      <span className="font-weight-bold text-dark">
                                        {formatCurrency(deal.remainingAmount || 0)}
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {/* Status and Created Date */}
                                <div className="d-flex justify-content-between align-items-center">
                                  <div className="d-flex align-items-center">
                                    <div>
                                      <small className="text-muted d-block">Step/Status</small>
                                      <div className="d-flex flex-column">
                                        <Badge 
                                          color="light" 
                                          className="badge mb-1"
                                          style={{ backgroundColor: '#f8f9fa', color: '#495057', border: '1px solid #dee2e6' }}
                                        >
                                          {deal.status || 'N/A'}
                                        </Badge>
                                         {deal.substatus && deal.status !== 'DECLINED' && (
                                           <Badge 
                                             color="light" 
                                             className="badge mb-1"
                                             style={{ backgroundColor: '#9ca3af', color: 'white', border: '1px solid #dee2e6', marginLeft: '-8px' }}
                                           >
                                             {deal.substatus}
                                           </Badge>
                                         )}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <small className="text-muted d-block">Created</small>
                                    <small className="font-weight-bold text-dark">
                                      {formatDate(deal.createdDate)}
                                    </small>
                                  </div>
                                </div>
                              </CardBody>
                            </Card>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ))}
                
                {/* Pagination */}
                {(deals.length > 0) && (
                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center px-3 py-2 border-top">
                    <div className="text-muted compact-text mb-2 mb-sm-0">
                      {totalElements > 0 
                        ? `Showing ${formatNumber(Math.min(currentPage * itemsPerPage + 1, totalElements))}-${formatNumber(Math.min((currentPage + 1) * itemsPerPage, totalElements))} of ${formatNumber(totalElements)}`
                        : `Showing ${formatNumber(deals.length)} deals`
                      }
                    </div>
                    <div className="d-flex align-items-center flex-wrap">
                      <Button
                        color="light"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={loading || currentPage === 0}
                        className="mr-1"
                        style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                      >
                        <i className="fas fa-chevron-left" />
                      </Button>
                      <div className="d-flex align-items-center">
                        {(() => {
                          const pages = [];
                          const maxVisiblePages = 5;
                          // Use totalPages directly from API response
                          const total = totalPages || 1;
                          
                          let startPage = Math.max(1, (currentPage + 1) - Math.floor(maxVisiblePages / 2));
                          let endPage = Math.min(total, startPage + maxVisiblePages - 1);
                          
                          // Adjust startPage if we don't have enough pages to show
                          if (endPage - startPage + 1 < maxVisiblePages && total >= maxVisiblePages) {
                            startPage = Math.max(1, endPage - maxVisiblePages + 1);
                          }
                          
                          for (let i = startPage; i <= endPage; i++) pages.push(i);
                          return pages.map(page => (
                            <Button
                              key={page}
                              color={page === (currentPage + 1) ? 'primary' : 'light'}
                              size="sm"
                              onClick={() => !loading && handlePageChange(page - 1)}
                              className="mx-1"
                              disabled={loading}
                              style={{ minWidth: '32px', padding: '4px 8px', fontSize: '0.75rem' }}
                            >
                              {page}
                            </Button>
                          ));
                        })()}
                      </div>
                      <Button
                        color="light"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={loading || (currentPage + 1 >= totalPages)}
                        className="ml-1"
                        style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                      >
                        <i className="fas fa-chevron-right" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default DealsList;
